import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, Users, Shield } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface StaffMember {
  user_id: string;
  email: string;
  role: string;
  permissions: string[];
}

const ALL_PERMISSIONS = [
  { value: "manage_documents", label: "Manage Documents" },
  { value: "manage_categories", label: "Manage Categories" },
  { value: "manage_faq", label: "Manage FAQ" },
  { value: "manage_staff", label: "Manage Staff" },
];

export default function StaffManagement() {
  const { toast } = useToast();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  async function fetchStaff() {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("manage-staff", {
      body: { action: "list" },
    });
    if (error) {
      toast({ title: "Error loading staff", description: error.message, variant: "destructive" });
    } else {
      setStaff(data.staff ?? []);
    }
    setLoading(false);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setCreating(true);

    const { data, error } = await supabase.functions.invoke("manage-staff", {
      body: { action: "create", email, permissions },
    });

    if (error || data?.error) {
      toast({ title: "Error", description: data?.error ?? error?.message, variant: "destructive" });
    } else {
      toast({ title: "Invite sent", description: "The staff member will receive an email to set their password." });
      setEmail("");
      setPermissions([]);
      fetchStaff();
    }
    setCreating(false);
  }

  async function handleDelete(userId: string) {
    // Optimistically remove from UI
    setStaff((prev) => prev.filter((m) => m.user_id !== userId));
    const { data, error } = await supabase.functions.invoke("manage-staff", {
      body: { action: "delete", user_id: userId },
    });
    if (error || data?.error) {
      toast({ title: "Error", description: data?.error ?? error?.message, variant: "destructive" });
      fetchStaff(); // revert on failure
    } else {
      toast({ title: "Staff account removed" });
    }
  }

  async function handleUpdatePermissions(userId: string, newPerms: string[]) {
    const { data, error } = await supabase.functions.invoke("manage-staff", {
      body: { action: "update_permissions", user_id: userId, permissions: newPerms },
    });
    if (error || data?.error) {
      toast({ title: "Error", description: data?.error ?? error?.message, variant: "destructive" });
    } else {
      toast({ title: "Permissions updated" });
      fetchStaff();
    }
  }

  function togglePermission(perm: string) {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  }

  return (
    <div className="space-y-6">
      {/* Create Staff Account */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Plus className="h-5 w-5" /> Invite Staff Member
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="staff@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="flex flex-wrap gap-4">
                {ALL_PERMISSIONS.map((p) => (
                  <label key={p.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={permissions.includes(p.value)}
                      onCheckedChange={() => togglePermission(p.value)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={creating}>
              {creating ? "Sending invite…" : "Send Invite"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Staff List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5" /> Staff Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
          ) : staff.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No staff accounts yet.</p>
          ) : (
            <div className="space-y-3">
              {staff.map((member) => (
                <div key={member.user_id} className="flex items-start justify-between gap-4 border border-border rounded-lg p-4 bg-background">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">{member.email}</p>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${member.role === "admin" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {member.role}
                      </span>
                    </div>
                    {member.role !== "admin" && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ALL_PERMISSIONS.map((p) => {
                          const hasIt = member.permissions.includes(p.value);
                          return (
                            <button
                              key={p.value}
                              onClick={() => {
                                const newPerms = hasIt
                                  ? member.permissions.filter((x) => x !== p.value)
                                  : [...member.permissions, p.value];
                                handleUpdatePermissions(member.user_id, newPerms);
                              }}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${hasIt ? "bg-primary/10 border-primary text-primary" : "bg-muted/50 border-border text-muted-foreground hover:border-primary/50"}`}
                            >
                              <Shield className="h-3 w-3 inline mr-1" />
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {member.role !== "admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-destructive transition-colors shrink-0" aria-label="Delete staff member">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete staff account?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete {member.email}'s account and all their permissions. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(member.user_id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}